import { Tabs, TabsList, TabsTrigger } from '@/components/Tabs/Tabs';
import { type OrderQueueParams } from '@/features/reports/api';
import { TRANSACTIONS_COPY as COPY } from '@/features/reports/copy';
import { type ReportPeriod } from '@/features/reports/periods';
import {
    TRANSACTIONS_VIEW,
    TRANSACTIONS_VIEWS,
    type TransactionsView,
} from '@/features/reports/tabs';
import { OrderQueueView } from '@/features/reports/transactions/OrderQueueView';
import { PeriodViews } from '@/features/reports/transactions/PeriodViews';
import { TaxView } from '@/features/reports/transactions/TaxView';

/**
 * Reports › Transactions (ALS-86): five views behind one tab, as the prototype puts its order
 * reports behind sub-tabs rather than six tabs across the top. Orders is a live queue with
 * filters of its own; Margin, Returns and Products are one period's figures from one request, so
 * moving between those three never asks again; Tax (ALS-90) is the filing cycles' reconciliation,
 * on the same period read as month, quarter or year.
 */
export function TransactionsPanel({
    period,
    view,
    queue,
    onPeriodChange,
    onViewChange,
    onQueueChange,
}: {
    period: ReportPeriod;
    view: TransactionsView;
    queue: OrderQueueParams;
    onPeriodChange: (period: ReportPeriod) => void;
    onViewChange: (view: TransactionsView) => void;
    onQueueChange: (queue: OrderQueueParams) => void;
}) {
    return (
        <div className="flex min-w-0 flex-col gap-(--layout-row-gap)">
            <Tabs value={view} onValueChange={(next) => onViewChange(next as TransactionsView)}>
                {/* Five labels hug their text and the strip scrolls sideways on a narrow phone,
                    fading the edge with more past it and keeping the chosen view in sight, as the
                    record pages' strips do. Shared out equally they ran into each other at 320
                    once Tax made five (ALS-90). */}
                <TabsList
                    size="sm"
                    aria-label={COPY.tabs.transactions}
                    scrollable
                    className="self-start"
                >
                    {TRANSACTIONS_VIEWS.map((v) => (
                        <TabsTrigger
                            key={v}
                            value={v}
                            className="flex-none pointer-coarse:min-w-11"
                        >
                            {COPY.views[v]}
                        </TabsTrigger>
                    ))}
                </TabsList>
            </Tabs>
            {view === TRANSACTIONS_VIEW.Orders ? (
                <OrderQueueView params={queue} onChange={onQueueChange} />
            ) : view === TRANSACTIONS_VIEW.Tax ? (
                <TaxView period={period} onPeriodChange={onPeriodChange} />
            ) : (
                <PeriodViews view={view} period={period} onPeriodChange={onPeriodChange} />
            )}
        </div>
    );
}
